<script setup lang="ts">
import type { TenantLandingConfig } from '~/types/types'

/**
 * FAQ accordion.
 *  - boxed: card per item on a tinted band (Classic / Split / Bold)
 *  - ruled: hairline-separated list, no cards (Editorial / Minimal)
 */
withDefaults(defineProps<{
  landing: TenantLandingConfig
  variant?: 'boxed' | 'ruled'
  displayClass?: string
}>(), { variant: 'boxed', displayClass: '' })

const { t } = useI18n()
const openFaq = ref<number | null>(null)
</script>

<template>
  <section
    v-if="landing.faq.length"
    :class="variant === 'boxed' ? 'bg-[var(--bg2)] border-y border-[var(--border)]' : ''"
  >
    <div
      class="mx-auto max-w-3xl px-6"
      :class="variant === 'boxed' ? 'py-20' : 'py-16'"
    >
      <h2
        class="text-3xl tracking-tight"
        :class="[displayClass, variant === 'boxed' ? 'text-center' : '']"
      >
        {{ t('site.faq_title') }}
      </h2>
      <div
        class="mt-10"
        :class="variant === 'boxed' ? 'space-y-2' : 'divide-y divide-[var(--border)] border-y border-[var(--border)]'"
      >
        <div
          v-for="(item, i) in landing.faq"
          :key="i"
          :class="variant === 'boxed' ? 'rounded-xl border border-[var(--border)] bg-[var(--bg)]' : ''"
        >
          <button
            type="button"
            class="flex w-full items-center justify-between gap-4 text-left"
            :class="variant === 'boxed' ? 'px-5 py-4' : 'py-4'"
            @click="openFaq = openFaq === i ? null : i"
          >
            <span class="font-medium">{{ item.question }}</span>
            <UIcon
              :name="openFaq === i ? 'i-lucide-minus' : 'i-lucide-plus'"
              class="size-5 ui-text-subtle shrink-0"
            />
          </button>
          <div
            v-if="openFaq === i"
            class="text-sm ui-text-subtle whitespace-pre-line"
            :class="variant === 'boxed' ? 'px-5 pb-4' : 'pb-4'"
          >
            {{ item.answer }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
